import {groupByUniqueKey, setDifference} from '@benchling/js-utils';
import produce from 'immer';
import _, {omit} from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {
  ExistingPlateDesign,
  FromServerNoteLinkedPlateDesign,
  InitialPlateDesign,
  NewPlateDesign,
  NoteLinkedPlateDesign,
  PlateDesign,
  PlateDesignData,
  PlateDesignInput,
  ToServerNoteLinkedPlateDesign,
  UpdatePlateDesign,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import {PlateDesignStatus} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import * as NoteLinkedDataUtil from 'notebook/notes/note-linked-objects/NoteLinkedDataUtil';
import type {CidIdMap} from 'notebook/notes/NoteConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import inspectAllNoteParts from 'notebook/notes/util/inspectAllNoteParts';
import {generateUniqueName} from 'notebook/util/GenerateUniqueName';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import type {PlateDesignState} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  getAnnotateFixedPlateInputsFromPlateMap,
  getAnnotatePlateMapWellInput,
  getMaterialInputForBioentity,
  getTestOrderInfosForEntity,
  getTransferToFixedPlateInputsFromPlateMap,
} from 'samples/plate-maps/AnnotateFillPlateUtil';
import {
  MeasurementLayerInput,
  PersistedPlateMap,
  PersistedPlateMapPosition,
  PlateMapMeasurement,
  PlateMapPositionInput,
} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  getDataParameterTags,
  getHydratedPlateData,
  getPersistedPlateMapAsPlateMap,
  getSchemaTags,
  isContainerSource,
  UnitsById,
} from 'samples/plate-maps/plate-record/PlateRecordUtil';
import {
  MeasurementInput,
  PlateMapWell,
  type PlateMap,
  type WellPosition,
  MeasurementLayer,
} from 'samples/plate-maps/PlateMapConstants';
import {wellPositionIterator} from 'samples/plate-maps/PlateMapUtil';
import {validatePlateDesign} from 'samples/plate-maps/validation/ValidationDAO';
import {PlateMapValidation} from 'samples/plate-maps/validation/ValidationTypes';
import {assertNever} from 'util/Types';

/**
 * Transform {@link FromServerNoteLinkedPlateDesign} into a {@link NoteLinkedPlateDesign}
 *
 * This is invoked by the client when loading a note into a document store
 * to support auto-saving logic. Notably, we replace ids with cids, which will
 * be rehydrated on save via {@link prepareForSave}.
 *
 * Additionally, we transform the plateDesign field to include a status field,
 * to track if it is the data shape from initial load or if the client has
 * modified it. See {@link PlateDesign} type for more details.
 *
 * @param item
 * @param cidIdMap
 */
export function prepareForStore(item: FromServerNoteLinkedPlateDesign, cidIdMap: CidIdMap): void {
  const plateDesign = item.data.plateDesign;
  const preparedItem = item as unknown as NoteLinkedPlateDesign;
  preparedItem.data.plateDesign = {
    status: PlateDesignStatus.INITIAL,
    data: plateDesign,
  } satisfies PlateDesign;

  // This reproduces the logic in StoreTransformUtil.addCid, but we can't use that function since it only
  // works with id/cid pairs: we also want similar behavior for versionId/versionCid pairs. It would be nice
  // if the helper was parameterized by the key to use for the id / cid fields.
  preparedItem.cid = _.uniqueId('note-linked-plate-design-');
  preparedItem.data.cid = _.uniqueId('plate-design-');
  preparedItem.data.versionCid = _.uniqueId('plate-design-version-');
  cidIdMap[preparedItem.cid] = item.id;
  cidIdMap[preparedItem.data.cid] = item.data.id;
  cidIdMap[preparedItem.data.versionCid] = item.data.versionId;
  delete preparedItem.id;
  delete preparedItem.data.id;
  delete preparedItem.data.versionId;
}

export function prepareForSave(
  item: NoteLinkedPlateDesign,
  cidIdMap: CidIdMap
): ToServerNoteLinkedPlateDesign {
  const base = NoteLinkedDataUtil.prepareForSave(item, cidIdMap);

  // Caller is expected to regenerate the cid if the plateMap is modified.
  const id = cidIdMap[item.data.cid];
  const versionId = cidIdMap[item.data.versionCid];
  const plateDesignInput = item.data.plateDesign.data.plateDesignInput;
  if (typeof id === 'string') {
    if (typeof versionId === 'string') {
      return {
        ...base,
        data: {
          id,
          cid: item.data.cid,
          versionId,
          versionCid: item.data.versionCid,
        } satisfies ExistingPlateDesign,
      };
    } else {
      assert(
        plateDesignInput != null,
        'plateDesignInput was unexpectedly missing for UpdatePlateDesign - document saving will fail.'
      );
      return {
        ...base,
        data: {
          id,
          cid: item.data.cid,
          versionCid: item.data.versionCid,
          versionId: null,
          plateDesign: plateDesignInput,
        } satisfies UpdatePlateDesign,
      };
    }
  } else {
    assert(!versionId, 'versionId must be undefined');
    assert(
      plateDesignInput != null,
      'plateDesignInput was unexpectedly missing for NewPlateDesign - document saving will fail.'
    );
    return {
      ...base,
      data: {
        id: null,
        cid: item.data.cid,
        versionId: null,
        versionCid: item.data.versionCid,
        plateDesign: plateDesignInput,
      } satisfies NewPlateDesign,
    };
  }
}

function getMeasurementLayerInput(measurementLayer: MeasurementLayer): MeasurementLayerInput {
  return {layer_id: measurementLayer.layerId, name: measurementLayer.name};
}

/**
 * Get a PlateDesignInput from an InitialPlateDesign.
 *
 * In the case that we are dealing with an InitialPlateDesign, we will need to do analogous transformations
 * to the various helpers used by `getPlateDesignInputForModifiedPlateDesign`.
 *
 * @param plateDesign
 * @returns
 */
export function getPlateDesignInputForInitialPlateDesign(
  initialPlateDesign: InitialPlateDesign
): PlateDesignInput {
  if (initialPlateDesign.plateDesignInput != null) {
    return initialPlateDesign.plateDesignInput;
  }

  function getMeasurementInput(measurement: PlateMapMeasurement | null): MeasurementInput {
    if (measurement == null) {
      return {value: null, unit: null};
    }
    return {
      value: measurement.value,
      unit: {
        unit_api_id: measurement.displayUnit.unitApiId,
        unit_version_number: measurement.displayUnit.unitVersionNumber,
      },
    };
  }

  return {
    name: initialPlateDesign.name,
    plateMap: {
      plate_schema_api_id: initialPlateDesign.plateMap.plateSchemaApiId,
      positions: initialPlateDesign.plateMap.positions.map(
        (position): PlateMapPositionInput => ({
          coordinates: position.coordinates,
          transfer_input: {
            sources: position.transfer.sources.map((source) => ({
              transfer_quantity: getMeasurementInput(source.transferQuantity),
              ...(isContainerSource(source)
                ? {
                    container_id: source.containerApiId,
                    contents: source.contents.map((content) => ({
                      entity_id: content.content.entityApiId,
                      final_concentration: getMeasurementInput(content.finalConcentration),
                      material_input:
                        content.materialInputParameterVersionId == null
                          ? null
                          : getMaterialInputForBioentity(
                              content.content.entityApiId,
                              content.materialInputParameterVersionId
                            ),
                      test_orders: getTestOrderInfosForEntity(content.testOrders),
                    })),
                  }
                : {
                    entity_id: source.entityApiId,
                    final_concentration: getMeasurementInput(source.finalConcentration),
                    source_concentration: getMeasurementInput(source.concentration),
                    material_input:
                      source.materialInputParameterVersionId == null
                        ? null
                        : getMaterialInputForBioentity(
                            source.entityApiId,
                            source.materialInputParameterVersionId
                          ),
                    test_orders: getTestOrderInfosForEntity(source.testOrders),
                  }),
            })),
            contents: position.transfer.contents.map((content) => ({
              entity_id: content.content.entityApiId,
              final_concentration: getMeasurementInput(content.finalConcentration),
              material_input:
                content.materialInputParameterVersionId == null
                  ? null
                  : getMaterialInputForBioentity(
                      content.content.entityApiId,
                      content.materialInputParameterVersionId
                    ),
              test_orders: getTestOrderInfosForEntity(content.testOrders),
            })),
            final_quantity: getMeasurementInput(position.transfer.finalQuantity),
          },
          annotate_input: {
            ...getAnnotatePlateMapWellInput({
              well: {
                role: position.role,
                schemaTags: getSchemaTags(position.schemaFieldValues),
                dataParameterTags: getDataParameterTags(position.dataParameterValues ?? []),
                concentrationLayers: {},
              },
              // There is no user selection of schema fields / data parameters as we are doing a deep copy, so
              // we pass in null to preserve all fields as referenced in the annotate_input.
              annotatedDataParameterIds: null,
              annotatedSchemaFieldIds: null,
              includeMeasurementLayers: false,
            }),
            // This is a hack to work around the fact that we haven't hydrated the plate map.
            // getAnnotatePlateMapWellInput expects hydrated measurement values, but we're working with an
            // unhydrated plate map. Since we're working with a persisted plate map, though, we have enough
            // information to construct the values directly. So, we omit them above and construct
            // them here manually.
            measurement_layer_values: (position.measurementLayerValues ?? []).map(
              (measurementLayerValue) => ({
                layer_id: measurementLayerValue.layerId,
                value: getMeasurementInput(measurementLayerValue.value),
              })
            ),
          },
        })
      ),
      measurement_layers: initialPlateDesign.plateMap.measurementLayers?.map((measurementLayer) =>
        getMeasurementLayerInput(measurementLayer)
      ),
    },
  };
}

export function getPlateDesignInputForModifiedPlateDesign({
  name,
  plateSchemaId,
  uiPlateMap,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
}: {
  name: string;
  plateSchemaId: string;
  uiPlateMap: PlateMap;
  annotatedDataParameterIds: Set<string>;
  annotatedSchemaFieldIds: Set<string>;
}): PlateDesignInput {
  const annotateInputs = getAnnotateFixedPlateInputsFromPlateMap({
    plateMap: uiPlateMap,
    annotatedDataParameterIds,
    annotatedSchemaFieldIds,
    includeMeasurementLayers: true,
  });
  const transferInputs = getTransferToFixedPlateInputsFromPlateMap(uiPlateMap);
  const getHash = (p: WellPosition): string => `${p.row},${p.column}`;
  const annotateByPosition = groupByUniqueKey(annotateInputs, ({position}) => getHash(position));
  const transferByPosition = groupByUniqueKey(transferInputs, ({position}) => getHash(position));
  const positions: Array<PlateMapPositionInput> = [];
  for (const coordinates of wellPositionIterator(uiPlateMap.size)) {
    const hash = getHash(coordinates);
    const annotateInput = annotateByPosition[hash] ? omit(annotateByPosition[hash], 'position') : null;
    const transferInput = transferByPosition[hash] ? omit(transferByPosition[hash], 'position') : null;
    positions.push({coordinates, annotate_input: annotateInput, transfer_input: transferInput});
  }
  return {
    name,
    plateMap: {
      plate_schema_api_id: plateSchemaId,
      positions,
      measurement_layers: Object.values(uiPlateMap.concentrationLayerInfoById ?? {}).map((measurementLayer) =>
        getMeasurementLayerInput(measurementLayer)
      ),
    },
  };
}

export function makeNoteLinkedPlateDesignData({
  plateDesignState,
  plateMap,
  plateDesignCid,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
  hasPlateRecords,
}: {
  plateDesignState: PlateDesignState & {name: string};
  plateMap: PlateMap;
  plateDesignCid: string | null;
  annotatedDataParameterIds: Set<string>;
  annotatedSchemaFieldIds: Set<string>;
  hasPlateRecords: boolean;
}): PlateDesignData {
  // Filter out layers that are not selected from plate map in optimistic render
  const filteredPlateMap = produce(plateMap, (draft) => {
    if (draft.wells.length === 0) {
      return;
    }
    const excludedParameters = setDifference(
      new Set(Object.keys(draft.wells[0].dataParameterTags)),
      new Set(annotatedDataParameterIds)
    );
    const excludedSchemaFields = setDifference(
      new Set(Object.keys(draft.wells[0].schemaTags)),
      annotatedSchemaFieldIds
    );
    for (const well of draft.wells) {
      well.dataParameterTags = omit(well.dataParameterTags, ...excludedParameters);
      well.schemaTags = omit(well.schemaTags, ...excludedSchemaFields);
    }
  });
  return {
    cid: plateDesignCid ?? _.uniqueId('plate-design'),
    versionCid: _.uniqueId('plate-design-version'),
    plateDesign: {
      status: PlateDesignStatus.MODIFIED,
      data: {
        plateMap: filteredPlateMap,
        plateDesignInput: getPlateDesignInputForModifiedPlateDesign({
          name: plateDesignState.name,
          plateSchemaId: plateDesignState.plateSchema!.id,
          uiPlateMap: plateMap,
          annotatedDataParameterIds,
          annotatedSchemaFieldIds,
        }),
      },
    },
    // This flag isn't written as part of patch (see prepareForSave), but is passed through to keep the
    // optimistic render consistent.
    hasPlateRecords,
  };
}

export function makeNoteLinkedPlateDesign(data: PlateDesignData): NoteLinkedPlateDesign {
  return {
    cid: _.uniqueId('note-linked-plate-design'),
    name: '', // Unused NLO field
    type: NoteLinkedObjectType.NOTE_LINKED_PLATE_DESIGN,
    data,
  };
}

export function getPlateDesignName(plateDesign: PlateDesign): string {
  return plateDesign.status === PlateDesignStatus.INITIAL
    ? plateDesign.data.name
    : plateDesign.data.plateDesignInput.name;
}

export async function getPlateDesignStalenessValidation(
  plateDesignId: string
): Promise<{plateMap: PlateMap; plateMapStalenessValidation: PlateMapValidation}> {
  const {sanitizedPlateMap, plateMapValidation} = await validatePlateDesign(plateDesignId);
  const {schemaIdsByReagentId, unitsById} = await getHydratedPlateData(
    sanitizedPlateMap.positions,
    'transfer'
  );
  const plateMap = getPersistedPlateMapAsPlateMap(sanitizedPlateMap, schemaIdsByReagentId, unitsById);
  return {plateMap, plateMapStalenessValidation: omit(plateMapValidation, 'reagentValueValidation')};
}

/**
 * Transform the given plate design to a plate map for display.
 */
export function getPlateDesignAsPlateMap(
  plateDesign: PlateDesign,
  schemaIdsByReagentId: {[id: string]: string | undefined},
  unitsById: UnitsById
): PlateMap {
  const {status} = plateDesign;
  switch (status) {
    case PlateDesignStatus.INITIAL: {
      // plateMap on the INITIAL plate design is one-to-one with API type, which is not shaped for UI usage.
      return getPersistedPlateMapAsPlateMap(plateDesign.data.plateMap, schemaIdsByReagentId, unitsById);
    }
    case PlateDesignStatus.MODIFIED:
      // If we've gotten here, the plateMap has already been constructed.
      return plateDesign.data.plateMap;
    default:
      assert(false, `Unexpected status: ${assertNever(status)}`);
  }
}

/**
 * Clear the contents and sources of a well and set the current and final quantities to null.
 */
function clearWellContentsAndSources(well: PlateMapWell): PlateMapWell {
  return produce(well, (draft) => {
    draft.sources = [];
    draft.contents = [];
    draft.quantity = {value: null, unit: null};
    draft.finalQuantity = {
      measurement: {value: null, unit: null},
      errors: {valueError: null, unitsError: null},
    };
  });
}

/**
 * Clear the sources and contents of a persisted plate map position and also set the final quantity to null.
 */
function clearPositionContentsAndSources(position: PersistedPlateMapPosition): PersistedPlateMapPosition {
  return produce(position, (draft) => {
    draft.transfer.sources = [];
    draft.transfer.contents = [];
    draft.transfer.finalQuantity = null;
  });
}

function clearPlateMapInputTransferInput(position: PlateMapPositionInput): PlateMapPositionInput {
  return produce(position, (draft) => {
    draft.transfer_input = null;
  });
}

/**
 * Copy a NoteLinkedPlateDesign so that it can be pasted elsewhere.
 *
 * This notably regenerates cids so that a new NLO, PlateDesign, and PlateDesignVersion are created / tracked.
 *
 * NOTE: In the cut-and-paste use case, we may want to re-use the same plate design (i.e. avoid clearing
 * the cid): if we start surfacing version history at the item level, the user may wish to see the
 * history of the plate design from before the cut. Supporting that is a bit more complex, requiring us to
 * enumerate this.props.note, and confirm that the plate design is not used elsewhere in the note.
 * This can be implemented in the future if Product wants this behavior. For reference, see:
 * DATA_TABLE_PLUGIN.itemFromHTML.
 *
 * This guarantees that plateDesignInput is defined, as it is required for saving a new PlateDesignVersion -
 * we'd rather fail fast at copy time than at save time.
 */
export function copyNoteLinkedPlateDesign({
  source,
  existingPlateDesignNames,
  clearContentsAndSources,
}: {
  source: NoteLinkedPlateDesign;
  existingPlateDesignNames: Array<string>;
  clearContentsAndSources?: boolean;
}): NoteLinkedPlateDesign {
  return produce(source, (draft) => {
    draft.cid = _.uniqueId('note-linked-plate-design-');
    draft.data.cid = _.uniqueId('plate-design-');
    draft.data.versionCid = _.uniqueId('plate-design-version-');
    draft.data.hasPlateRecords = false; // This won't be refetched from the server, so manually reset it.
    // NB: We use source instead of draft so that we can keep Immer draft usage contained to this function.
    const copiedPlateDesignName = generateUniqueName(
      existingPlateDesignNames,
      `${getPlateDesignName(source.data.plateDesign)} (copy)`,
      {
        allowNoSuffix: true,
        addSpaceAfterPrefix: true,
      }
    );
    if (draft.data.plateDesign.status === PlateDesignStatus.INITIAL) {
      // For initial plate designs, we need to set the name on both the design and the input.
      draft.data.plateDesign.data.name = copiedPlateDesignName;
      if (clearContentsAndSources) {
        const sourcePlateMap = source.data.plateDesign.data.plateMap as PersistedPlateMap;
        draft.data.plateDesign.data.plateMap.positions = sourcePlateMap.positions.map((position) =>
          clearPositionContentsAndSources(position)
        );
      }
      draft.data.plateDesign.data.plateDesignInput = getPlateDesignInputForInitialPlateDesign(
        // Source type will match draft type.
        source.data.plateDesign.data as InitialPlateDesign
      );
      draft.data.plateDesign.data.plateDesignInput.name = copiedPlateDesignName;
    } else {
      // Modified plate designs only store the name on the input shape.
      draft.data.plateDesign.data.plateDesignInput.name = copiedPlateDesignName;
      if (clearContentsAndSources) {
        const sourcePlateMap = source.data.plateDesign.data.plateMap as PlateMap;
        draft.data.plateDesign.data.plateMap.wells = sourcePlateMap.wells.map((well) =>
          clearWellContentsAndSources(well)
        );
      }
    }
    assert(draft.data.plateDesign.data.plateDesignInput != null, 'Expected plateDesignInput to be defined');
    // Clear the transfer input of the plate map of plateDesignInput since this is where the contents and sources
    // are stored.
    if (clearContentsAndSources) {
      const plateMapInput = draft.data.plateDesign.data.plateDesignInput.plateMap;
      draft.data.plateDesign.data.plateDesignInput.plateMap.positions = plateMapInput.positions.map(
        (position) => clearPlateMapInputTransferInput(position)
      );
    }
  });
}

export function getAllPlateDesignNames(note: Note): Array<string> {
  const names: Array<string> = [];
  inspectAllNoteParts(note.noteParts, (notePart) => {
    if (getNotePartType(notePart) === ModelTypeConstants.NOTE_LINKED_PLATE_DESIGN) {
      names.push(getPlateDesignName((notePart.item as NoteLinkedPlateDesign).data.plateDesign));
    }
  });
  return names;
}
